import styled, { css } from "styled-components";

import { colorTheme, gridSpacing } from "@utils";

import type { TableV2Align } from "./types";

export const TableContainer = styled.div`
  width: 100%;
  min-width: 0;
  border-radius: 5px;
  background: ${colorTheme("white")};
`;

export const TableFrame = styled.div`
  border-radius: 8px;
  overflow: hidden;
`;

export const TableTitle = styled.div`
  margin-bottom: ${gridSpacing[4]}px;
  font-size: 18px;
  font-weight: 600;
`;

export const ScrollArea = styled.div<{ $maxBodyHeight?: number }>`
  position: relative;
  overflow-x: auto;
  contain: inline-size;
  ${({ $maxBodyHeight }) =>
    $maxBodyHeight &&
    css`
      max-height: ${$maxBodyHeight}px;
      overflow-y: auto;
    `}
`;

export const Grid = styled.div`
  width: 100%;
`;

export const HeaderGroup = styled.div`
  position: sticky;
  top: 0;
  z-index: 3;
`;

export const HeaderRow = styled.div`
  display: flex;
  font-weight: bold;
  font-size: 12px;
  color: ${colorTheme("neutralL1")};
  text-transform: uppercase;
  background: ${colorTheme("neutralL6")};
`;

export const BodyRow = styled.div<{
  $isExpanded?: boolean;
  $emphasis?: boolean;
  $rowHeight?: number;
}>`
  display: flex;
  font-weight: ${({ $emphasis }) => ($emphasis ? 600 : "normal")};

  > [role="cell"],
  > [data-spacer] {
    min-height: ${({ $rowHeight }) => $rowHeight ?? 0}px;
    border-bottom: ${({ $isExpanded }) =>
      $isExpanded ? "none" : `1px solid ${colorTheme("neutralL4")}`};
    background: ${colorTheme("white")};
  }

  &:hover > [role="cell"],
  &:hover > [data-spacer] {
    background: ${colorTheme("neutralL5")};
  }
`;

const pinnedShadow = {
  start: "inset -1px 0 0",
  end: "inset 1px 0 0",
};

export const CellBox = styled.div<{
  $align: TableV2Align;
  $pinned: false | "start" | "end";
  $isLastStart?: boolean;
  $isFirstEnd?: boolean;
  $overflow?: boolean;
  $wrap?: boolean;
  $isHeader?: boolean;
  $dropIndicator?: "before" | "after";
  $dragging?: boolean;
}>`
  position: ${({ $pinned }) => ($pinned ? "sticky" : "relative")};
  z-index: ${({ $pinned }) => ($pinned ? 2 : 1)};
  display: flex;
  align-items: center;
  justify-content: ${({ $align }) =>
    $align === "right"
      ? "flex-end"
      : $align === "center"
        ? "center"
        : "flex-start"};
  margin: 0;
  padding: ${gridSpacing[2]}px ${gridSpacing[3]}px;
  white-space: ${({ $wrap }) => ($wrap ? "normal" : "nowrap")};
  ${({ $align }) => $align === "right" && "font-variant-numeric: tabular-nums;"}
  overflow-x: ${({ $overflow }) => ($overflow ? "visible" : "hidden")};
  text-overflow: ellipsis;
  box-sizing: border-box;
  ${({ $isHeader }) => $isHeader && `background: ${colorTheme("neutralL6")};`}
  ${({ $isLastStart }) =>
    $isLastStart &&
    `box-shadow: ${pinnedShadow.start} ${colorTheme("neutralL4")};`}
  ${({ $isFirstEnd }) =>
    $isFirstEnd &&
    `box-shadow: ${pinnedShadow.end} ${colorTheme("neutralL4")};`}
  ${({ $dropIndicator }) =>
    $dropIndicator &&
    `box-shadow: inset ${$dropIndicator === "before" ? "3px" : "-3px"} 0 0 ${colorTheme("primary")};`}
  ${({ $dragging }) => $dragging && "opacity: 0.5;"}

  /* Header menu shows on hover or keyboard focus so headers stay clean at rest */
  [data-column-menu] {
    opacity: 0;
  }

  &:hover [data-column-menu],
  &:focus-within [data-column-menu] {
    opacity: 1;
  }
`;

// Absolutely positioned so it takes no space; sits opposite the header text
export const HeaderMenuSlot = styled.span<{ $side: "left" | "right" }>`
  position: absolute;
  top: 50%;
  ${({ $side }) =>
    $side === "left"
      ? `left: ${gridSpacing[1]}px;`
      : `right: ${gridSpacing[2]}px;`}
  display: flex;
  transform: translateY(-50%);
  background: ${colorTheme("neutralL6")};
`;

export const CellText = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  overflow-wrap: anywhere;
`;

export const SortLabel = styled.div<{ $canSort: boolean }>`
  display: flex;
  align-items: center;
  gap: ${gridSpacing[1]}px;
  min-width: 0;
  cursor: ${({ $canSort }) => ($canSort ? "pointer" : "default")};
  user-select: none;
`;

export const Resizer = styled.div<{ $active: boolean }>`
  position: absolute;
  top: 10%;
  right: 0;
  width: 4px;
  height: 80%;
  border-left: 3px dashed
    ${({ $active }) => ($active ? colorTheme("neutralL2") : "transparent")};
  cursor: col-resize;
  touch-action: none;

  &:hover {
    border-color: ${colorTheme("neutralL2")};
  }
`;

export const ExpandedPanel = styled.div`
  padding: ${gridSpacing[3]}px;
  border-bottom: 1px solid ${colorTheme("neutralL4")};
  background: ${colorTheme("white")};
`;

export const StateMessage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  gap: ${gridSpacing[2]}px;
  min-height: 80px;
  padding: ${gridSpacing[4]}px;
  color: ${colorTheme("neutral")};
  text-align: center;
`;

export const Pagination = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${gridSpacing[3]}px;
  min-height: 46px;
  padding: 0 ${gridSpacing[3]}px;
  background: ${colorTheme("neutralL6")};
  color: ${colorTheme("neutral")};
  font-size: 14px;

  strong {
    font-weight: 600;
  }
`;

export const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: ${gridSpacing[3]}px;

  > span:last-child {
    display: flex;
    gap: ${gridSpacing[1]}px;
  }
`;

export const PageSizeSelect = styled.div`
  width: 92px;
`;

export const ExportBar = styled.div`
  position: relative;
  width: 100%;

  > div {
    position: absolute;
    right: 0;
    z-index: 4;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    background: ${colorTheme("neutralL6")};
  }
`;

export const HeaderActions = styled.div`
  position: sticky;
  right: 0;
  z-index: 3;
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: flex-end;
  gap: ${gridSpacing[1]}px;
  padding: 0 ${gridSpacing[2]}px 0 ${gridSpacing[3]}px;
  box-sizing: border-box;
  background: linear-gradient(
    to right,
    transparent,
    ${colorTheme("neutralL6")} ${gridSpacing[2]}px
  );
`;

export const Tray = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

export const TrayToggle = styled.div<{ $open: boolean }>`
  display: flex;
  opacity: ${({ $open }) => ($open ? 0 : 1)};
  transition: opacity 150ms ease;
`;

export const TrayItems = styled.div<{ $open: boolean }>`
  position: absolute;
  top: 50%;
  right: 0;
  display: flex;
  align-items: center;
  gap: ${gridSpacing[1]}px;
  padding-left: ${gridSpacing[4]}px;
  background: linear-gradient(
    to right,
    transparent,
    ${colorTheme("neutralL6")} ${gridSpacing[3]}px
  );
  transform: translateY(-50%);
  clip-path: inset(0 0 0 ${({ $open }) => ($open ? "0" : "100%")});
  visibility: ${({ $open }) => ($open ? "visible" : "hidden")};
  pointer-events: ${({ $open }) => ($open ? "auto" : "none")};
  transition:
    clip-path 150ms ease,
    visibility 0s linear ${({ $open }) => ($open ? "0s" : "150ms")};
`;

export const FooterRow = styled.div`
  position: sticky;
  bottom: 0;
  z-index: 3;
  display: flex;
  font-weight: 600;
  background: ${colorTheme("neutralL6")};
  border-top: 1px solid ${colorTheme("neutralL4")};
`;
